import {
  acceptOrgInviteSchema, createOrgInviteSchema, orgInviteAcceptResponseSchema,
  orgInviteCreateResponseSchema, orgInviteListResponseSchema, orgInviteRevokeResponseSchema,
  type CreateOrgInvitePayload,
} from "@io-spoke/contracts/orgs";
import { getApiClient } from "./apiClient";
import { getActiveOrgId } from "./authStorage";

function invitationPath(orgId: string) {
  if (!orgId || getActiveOrgId() !== orgId) throw new Error("Select this workspace before managing its invitations.");
  return `/orgs/${encodeURIComponent(orgId)}/invites`;
}

export async function listOrgInvites(orgId: string) {
  const response = await getApiClient().get(invitationPath(orgId));
  const parsed = orgInviteListResponseSchema.safeParse(response.data);
  if (!parsed.success || parsed.data.invites.some((invite) => invite.orgId !== orgId)) throw new Error("Invitations returned an invalid workspace response.");
  return parsed.data.invites;
}

export async function createOrgInvite(orgId: string, payload: CreateOrgInvitePayload) {
  const request = createOrgInviteSchema.safeParse(payload);
  if (!request.success) throw new Error(request.error.issues[0]?.message ?? "Check the invitation details.");
  const response = await getApiClient().post(invitationPath(orgId), request.data);
  const parsed = orgInviteCreateResponseSchema.safeParse(response.data);
  if (!parsed.success || parsed.data.invite.orgId !== orgId) throw new Error("Invitation creation returned an invalid workspace response.");
  return parsed.data;
}

export async function revokeOrgInvite(orgId: string, inviteId: string) {
  if (!inviteId) throw new Error("Choose the invitation to revoke.");
  const response = await getApiClient().delete(`${invitationPath(orgId)}/${encodeURIComponent(inviteId)}`);
  const parsed = orgInviteRevokeResponseSchema.safeParse(response.data);
  if (!parsed.success || parsed.data.invite.orgId !== orgId || parsed.data.invite.id !== inviteId) throw new Error("Invitation revocation returned an invalid response.");
  return parsed.data.invite;
}

export async function acceptOrgInvite(token: string) {
  const request = acceptOrgInviteSchema.safeParse({ token });
  if (!request.success) throw new Error("This invitation link is missing its token. Ask the workspace administrator for a new link.");
  const response = await getApiClient().post("/orgs/invites/accept", request.data);
  const parsed = orgInviteAcceptResponseSchema.safeParse(response.data);
  if (!parsed.success || parsed.data.membership.orgId !== parsed.data.org.id || parsed.data.membership.status !== "active") throw new Error("Invitation acceptance returned an invalid workspace response.");
  return parsed.data;
}
